import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/vue'
import { onClickOutside } from '@vueuse/core'

import type { CSSProperties, Ref } from 'vue'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import type { Lesson } from './interfaces'

import { Status as BookingStatus } from '@brighterly/lib-core-types/booking/enums'

import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons'

import { resolveUrl } from '@shared/lib/url'
import { AppRoute } from '@shared/config'

import { LessonEntryAction } from './enums'
import { useLessonApproveJoinApi } from '../../api/lessons/approve-join.hook'
import { useLessonPrivileges } from './lesson-privileges.hook'
import { useLessonsRefresh } from './lessons-refresh.hook'
import {
  LESSON_ACTION_QUERY,
  LESSON_DELETE_RESERVED_HINT,
  canApproveLessonJoin,
  getLessonChangeHint,
  isReservedLesson,
} from './lessons'

interface LessonActionsOptions {
  lesson: ComputedRef<Lesson>
  editUrl: ComputedRef<string>
}

interface LessonActionsReturn {
  isOpen: Ref<boolean>
  containerRef: Ref<HTMLElement | null>
  floatingRef: Ref<HTMLElement | null>
  floatingStyles: Ref<CSSProperties>
  triggerIcon: IconDefinition
  refCanApproveJoin: ComputedRef<boolean>
  refCanViewChangelog: ComputedRef<boolean>
  refCanDelete: ComputedRef<boolean>
  refIsDeleteDisabled: ComputedRef<boolean>
  deleteHint: string
  refCanManage: ComputedRef<boolean>
  refIsChangeDisabled: ComputedRef<boolean>
  refChangeHint: ComputedRef<string>
  toggle: () => void
  openEdit: () => Promise<void>
  approveJoin: () => Promise<void>
  openChangelog: () => Promise<void>
  openDelete: () => Promise<void>
  openReschedule: () => Promise<void>
  openCancel: () => Promise<void>
}

/** Row menu of a lesson. */
export const useLessonActions = ({ lesson, editUrl }: LessonActionsOptions): LessonActionsReturn => {
  const route = useRoute()
  const { canDeleteBookings, canViewPaidBookings, canManageBookings } = useLessonPrivileges()
  const { refreshLessons } = useLessonsRefresh()

  const isOpen = ref(false)
  const containerRef = ref<HTMLElement | null>(null)
  const floatingRef = ref<HTMLElement | null>(null)

  const { floatingStyles } = useFloating(containerRef, floatingRef, {
    placement: 'bottom-end',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      shift({ padding: 8 }),
    ],
  })

  const refCanApproveJoin = computed<boolean>(() => canApproveLessonJoin(lesson.value))

  /** The changelog needs view-paid-bookings even for demo lessons; sales agents do not hold it. */
  const refCanViewChangelog = computed<boolean>(() => canViewPaidBookings.value)

  const refCanDelete = computed<boolean>(() => canDeleteBookings.value)

  const refIsDeleteDisabled = computed<boolean>(() => isReservedLesson(lesson.value.status))

  const refCanManage = computed<boolean>(() => canManageBookings.value)

  /** A completed or reserved lesson can be neither canceled nor rescheduled, as in the legacy menu. */
  const refIsChangeDisabled = computed<boolean>(() =>
    lesson.value.status === BookingStatus.Completed || isReservedLesson(lesson.value.status))

  const refChangeHint = computed<string>(() => getLessonChangeHint(lesson.value.status))

  const toggle = (): void => {
    isOpen.value = !isOpen.value
  }

  const openEdit = async (): Promise<void> => {
    isOpen.value = false
    await navigateTo(editUrl.value)
  }

  const approveJoin = async (): Promise<void> => {
    isOpen.value = false

    const isApproved = await useLessonApproveJoinApi({
      disciplineId: route.params.id as string,
      lessonId: lesson.value.id,
    })

    if (isApproved) await refreshLessons()
  }

  const openChangelog = async (): Promise<void> => {
    isOpen.value = false

    const url = resolveUrl(AppRoute.BookingsChangelog, { id: lesson.value.id })
    if (url) await navigateTo(url)
  }

  /** Cancel, reschedule and delete live on the lesson page: the row leads there with the window named. */
  const openOnPage = async (action: LessonEntryAction): Promise<void> => {
    isOpen.value = false
    await navigateTo({ path: editUrl.value, query: { [LESSON_ACTION_QUERY]: action } })
  }

  const openDelete = (): Promise<void> => openOnPage(LessonEntryAction.Delete)

  const openReschedule = (): Promise<void> => openOnPage(LessonEntryAction.Reschedule)

  const openCancel = (): Promise<void> => openOnPage(LessonEntryAction.Cancel)

  onClickOutside(containerRef, () => {
    isOpen.value = false
  })

  return {
    isOpen,
    containerRef,
    floatingRef,
    floatingStyles,
    triggerIcon: faEllipsisVertical,
    refCanApproveJoin,
    refCanViewChangelog,
    refCanDelete,
    refIsDeleteDisabled,
    deleteHint: LESSON_DELETE_RESERVED_HINT,
    refCanManage,
    refIsChangeDisabled,
    refChangeHint,
    toggle,
    openEdit,
    approveJoin,
    openChangelog,
    openDelete,
    openReschedule,
    openCancel,
  }
}
